import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OperatorAdminService } from '../../../services/operator-admin/operator-admin.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { CsvColumn, downloadCsv } from '../../../common-files/csv';
import { downloadPdf } from '../../../common-files/pdf';

// columns shared by the CSV and PDF exports
const BOOKING_COLUMNS: CsvColumn[] = [
  { key: 'bookingId', label: 'Booking' },
  { key: 'familyCode', label: 'Family code' },
  { key: 'guestName', label: 'Primary guest' },
  { key: 'type', label: 'Type' },
  { key: 'mode', label: 'Mode' },
  { key: 'fromDate', label: 'From' },
  { key: 'toDate', label: 'To' },
  { key: 'guestCount', label: 'Guests' },
  { key: 'status', label: 'Status' },
  { key: 'lifecycleStatus', label: 'Stage' },
];

const ALLOCATION_COLUMNS: CsvColumn[] = [
  { key: 'guestHouse', label: 'Guest house' },
  { key: 'room', label: 'Room / hall' },
  { key: 'accommoType', label: 'Type' },
  { key: 'capacity', label: 'Capacity' },
  { key: 'bookingId', label: 'Booking' },
  { key: 'familyCode', label: 'Family code' },
  { key: 'status', label: 'Status' },
  { key: 'lifecycleStatus', label: 'Stage' },
];

// bookings, room allocations and a status summary for one utsav, with
// CSV / PDF export. read-only: anyone with the utsav view can open it.
@Component({
  selector: 'app-oa-utsav-reports-dialog',
  standalone: true,
  imports: [CommonModule, LoaderComponent],
  templateUrl: './oa-utsav-reports-dialog.component.html',
  styleUrls: ['./oa-utsav-reports-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class OaUtsavReportsDialogComponent implements OnInit {
  isLoading: boolean = false;
  activeTab: 'bookings' | 'allocations' = 'bookings';
  summary: any = null;
  requisitions: any[] = [];
  allocations: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<OaUtsavReportsDialogComponent>,
    private operatorAdminService: OperatorAdminService,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) public data: { utsavInstanceId: number; title: string }
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.operatorAdminService.getUtsavReport(this.data.utsavInstanceId).subscribe({
      next: (res: any) => {
        const report = res?.data || {};
        this.summary = report.summary || null;
        this.requisitions = report.requisitions || [];
        this.allocations = report.allocations || [];
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load the utsav report.');
      },
    });
  }

  setTab(tab: 'bookings' | 'allocations'): void {
    this.activeTab = tab;
  }

  async export(format: 'csv' | 'pdf'): Promise<void> {
    const bookingsTab = this.activeTab === 'bookings';
    const rows = bookingsTab ? this.requisitions : this.allocations;
    if (!rows.length) {
      this.toastService.error('Nothing to export.');
      return;
    }
    const columns = bookingsTab ? BOOKING_COLUMNS : ALLOCATION_COLUMNS;
    const kind = bookingsTab ? 'bookings' : 'allocations';
    const fileName = `utsav-${this.data.utsavInstanceId}-${kind}`;
    if (format === 'csv') downloadCsv(fileName, columns, rows);
    else
      await downloadPdf(
        fileName,
        `${this.data.title || 'Utsav'} — ${kind}`,
        columns,
        rows,
        `Utsav #${this.data.utsavInstanceId}`
      );
  }

  close(): void {
    this.dialogRef.close();
  }
}
